/**
 * perfilAlunoBuilder — sub-container do slice das tabs do perfil do Aluno.
 *
 * Escopo: documento/requerimento/protocolo/observação + status + bloqueio acesso
 * + visão 360 + nota + boletim + frequência. Cobre `AlunoDetailView` tabs e
 * `useAlunoPerfil*` composables.
 *
 * Extraído de `container.ts` na Sprint F.5-polish-extras-4.
 *
 * @since F.5-polish-extras-4
 */
import type { $Fetch } from 'ofetch';

import { HttpDocumentoGateway } from '@/infrastructure/http/HttpDocumentoGateway';
import { HttpRequerimentoGateway } from '@/infrastructure/http/HttpRequerimentoGateway';
import { HttpProtocoloGateway } from '@/infrastructure/http/HttpProtocoloGateway';
import { HttpObservacaoAlunoGateway } from '@/infrastructure/http/HttpObservacaoAlunoGateway';
import { HttpAlunoStatusGateway } from '@/infrastructure/http/HttpAlunoStatusGateway';
import { HttpAlunoBloqueioAcessoGateway } from '@/infrastructure/http/HttpAlunoBloqueioAcessoGateway';
import { HttpAlunoVisao360Gateway } from '@/infrastructure/http/HttpAlunoVisao360Gateway';
import { HttpNotaGateway } from '@/infrastructure/http/HttpNotaGateway';
import { HttpBoletimGateway } from '@/infrastructure/http/HttpBoletimGateway';
import { HttpFrequenciaGateway } from '@/infrastructure/http/HttpFrequenciaGateway';

import {
  ListDocumentosAlunoUseCaseImpl, ListDocumentoTiposUseCaseImpl,
  ListRequerimentosAlunoUseCaseImpl, ListProtocolosAlunoUseCaseImpl,
  ListObservacoesAlunoUseCaseImpl, CreateObservacaoAlunoUseCaseImpl,
} from '@/application/usecase/PerfilAlunoTabsUseCaseImpl';
import {
  AlterarStatusAlunoUseCaseImpl, ListAlunoStatusHistoricoUseCaseImpl,
} from '@/application/usecase/AlunoStatusUseCaseImpl';
import {
  BloquearAcessoAlunoUseCaseImpl, DesbloquearAcessoAlunoUseCaseImpl,
  ListBloqueiosAcessoUseCaseImpl, FindBloqueioAtivoUseCaseImpl,
} from '@/application/usecase/AlunoBloqueioAcessoUseCaseImpl';
import { GetAlunoVisao360 } from '@/application/usecase/GetAlunoVisao360';
import {
  ListNotasByMatriculaUseCaseImpl, LancarNotaUseCaseImpl,
} from '@/application/usecase/NotaUseCaseImpl';
import { GetBoletim } from '@/application/usecase/GetBoletim';
import {
  ListFrequenciasByMatriculaUseCaseImpl, LancarFrequenciaUseCaseImpl,
} from '@/application/usecase/FrequenciaUseCaseImpl';

import type { DocumentoGateway } from '@/application/port/out/DocumentoGateway';
import type { RequerimentoGateway } from '@/application/port/out/RequerimentoGateway';
import type { ProtocoloGateway } from '@/application/port/out/ProtocoloGateway';
import type { ObservacaoAlunoGateway } from '@/application/port/out/ObservacaoAlunoGateway';
import type { AlunoStatusGateway } from '@/application/port/out/AlunoStatusGateway';
import type { AlunoBloqueioAcessoGateway } from '@/application/port/out/AlunoBloqueioAcessoGateway';
import type { AlunoVisao360Gateway } from '@/application/port/out/AlunoVisao360Gateway';
import type { NotaGateway } from '@/application/port/out/NotaGateway';
import type { BoletimGateway } from '@/application/port/out/BoletimGateway';
import type { FrequenciaGateway } from '@/application/port/out/FrequenciaGateway';

export function buildPerfilAlunoSlice(httpClient: $Fetch) {
  const documentoGateway: DocumentoGateway = new HttpDocumentoGateway(httpClient);
  const requerimentoGateway: RequerimentoGateway = new HttpRequerimentoGateway(httpClient);
  const protocoloGateway: ProtocoloGateway = new HttpProtocoloGateway(httpClient);
  const observacaoAlunoGateway: ObservacaoAlunoGateway = new HttpObservacaoAlunoGateway(httpClient);
  const alunoStatusGateway: AlunoStatusGateway = new HttpAlunoStatusGateway(httpClient);
  const alunoBloqueioAcessoGateway: AlunoBloqueioAcessoGateway = new HttpAlunoBloqueioAcessoGateway(httpClient);
  const alunoVisao360Gateway: AlunoVisao360Gateway = new HttpAlunoVisao360Gateway(httpClient);
  const notaGateway: NotaGateway = new HttpNotaGateway(httpClient);
  const boletimGateway: BoletimGateway = new HttpBoletimGateway(httpClient);
  const frequenciaGateway: FrequenciaGateway = new HttpFrequenciaGateway(httpClient);

  const useCases = {
    listDocumentosAluno: new ListDocumentosAlunoUseCaseImpl(documentoGateway),
    listDocumentoTipos: new ListDocumentoTiposUseCaseImpl(documentoGateway),
    listRequerimentosAluno: new ListRequerimentosAlunoUseCaseImpl(requerimentoGateway),
    listProtocolosAluno: new ListProtocolosAlunoUseCaseImpl(protocoloGateway),
    listObservacoesAluno: new ListObservacoesAlunoUseCaseImpl(observacaoAlunoGateway),
    createObservacaoAluno: new CreateObservacaoAlunoUseCaseImpl(observacaoAlunoGateway),
    alterarStatusAluno: new AlterarStatusAlunoUseCaseImpl(alunoStatusGateway),
    listAlunoStatusHistorico: new ListAlunoStatusHistoricoUseCaseImpl(alunoStatusGateway),
    bloquearAcessoAluno: new BloquearAcessoAlunoUseCaseImpl(alunoBloqueioAcessoGateway),
    desbloquearAcessoAluno: new DesbloquearAcessoAlunoUseCaseImpl(alunoBloqueioAcessoGateway),
    listBloqueiosAcesso: new ListBloqueiosAcessoUseCaseImpl(alunoBloqueioAcessoGateway),
    findBloqueioAtivo: new FindBloqueioAtivoUseCaseImpl(alunoBloqueioAcessoGateway),
    getAlunoVisao360: new GetAlunoVisao360(alunoVisao360Gateway),
    listNotasByMatricula: new ListNotasByMatriculaUseCaseImpl(notaGateway),
    lancarNota: new LancarNotaUseCaseImpl(notaGateway),
    getBoletim: new GetBoletim(boletimGateway),
    listFrequenciasByMatricula: new ListFrequenciasByMatriculaUseCaseImpl(frequenciaGateway),
    lancarFrequencia: new LancarFrequenciaUseCaseImpl(frequenciaGateway),
  };

  const gateways = {
    documentoGateway, requerimentoGateway, protocoloGateway, observacaoAlunoGateway,
    alunoStatusGateway, alunoBloqueioAcessoGateway, alunoVisao360Gateway,
    notaGateway, boletimGateway, frequenciaGateway,
  };

  return { useCases, gateways };
}
